import React, { useEffect, useState, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Box, Button, Typography, Paper, Fade } from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'
import { TOUR_STEPS } from './steps'


const CURSOR_MS = 400
const TOOLTIP_DELAY_MS = 650


function getTooltipPosition(rect) {
	if (!rect) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }

	const pad = 20
	const tw = 370
	const th = 300
	const vw = window.innerWidth
	const vh = window.innerHeight

	if (rect.right + pad + tw < vw) {
		return { top: Math.max(pad, Math.min(rect.top, vh - th - pad)), left: rect.right + pad }
	}
	if (rect.left - pad - tw > 0) {
		return { top: Math.max(pad, Math.min(rect.top, vh - th - pad)), left: rect.left - pad - tw }
	}
	if (rect.bottom + pad + th < vh) {
		return { top: rect.bottom + pad, left: Math.max(pad, Math.min(rect.left, vw - tw - pad)) }
	}
	return { top: Math.max(pad, rect.top - th - pad), left: Math.max(pad, Math.min(rect.left, vw - tw - pad)) }
}


function CursorSVG() {
	return <svg width="28" height="32" viewBox="0 0 16 19" style={{ filter: 'drop-shadow(2px 3px 4px rgba(0,0,0,0.4))' }}>
		<path
			d="M1.5 1 L1.5 15.5 L5 11.5 L8 17.5 L10 16.5 L7 10.5 L11.5 10.5 Z"
			fill="white" stroke="#222" strokeWidth="0.9" strokeLinejoin="round"
		/>
	</svg>
}


export function TourOverlay({ onClose, steps: externalSteps, modalRanges = [] }) {
	const tourSteps = externalSteps || TOUR_STEPS
	const [step, setStep] = useState(0)
	const [targetRect, setTargetRect] = useState(null)
	const [showTooltip, setShowTooltip] = useState(false)
	const [isClicking, setIsClicking] = useState(false)
	const [waiting, setWaiting] = useState(false)
	const pollRef = useRef(null)

	const current = tourSteps[step]
	const isField = current.type === 'field'
	const totalSteps = tourSteps.length

	// displayedStep tracks which step's content is actually visible in the tooltip.
	// It updates only after the fade-out completes (300ms = Fade timeout), preventing
	// the flicker where new content briefly appears before the animation starts.
	const [displayedStep, setDisplayedStep] = useState(0)
	const displayedCurrent = tourSteps[displayedStep]
	const isDisplayedField = displayedCurrent.type === 'field'

	// Find next valid step, skipping steps whose skipIf returns true
	const resolveStep = useCallback((from, dir = 1) => {
		let s = from + dir
		while (s >= 0 && s < totalSteps) {
			if (!tourSteps[s].skipIf?.()) return s
			s += dir
		}
		return null
	}, [tourSteps, totalSteps])

	// Cleanup polling and step resources on unmount
	useEffect(() => {
		return () => {
			if (pollRef.current) clearInterval(pollRef.current)
			tourSteps.forEach(s => s.cleanup?.())
		}
	}, [])

	useEffect(() => {
		// Auto-skip step if condition met
		if (current.skipIf?.()) {
			const next = resolveStep(step)
			next !== null ? setStep(next) : onClose()
			return
		}

		setShowTooltip(false)
		setIsClicking(false)
		setWaiting(false)

		// Update displayed content only after fade-out completes (300ms = Fade timeout)
		// so the tooltip never shows the next step's content while still fading out.
		const FADE_OUT_MS = 200
		const tDisplay = setTimeout(() => setDisplayedStep(step), FADE_OUT_MS)

		if (!isField) {
			setTargetRect(null)
			const t = setTimeout(() => setShowTooltip(true), FADE_OUT_MS)
			return () => { clearTimeout(tDisplay); clearTimeout(t) }
		}

		const el = current.findTarget?.()
		if (el) {
			el.scrollIntoView({ behavior: 'smooth', block: 'center' })
			// Wait for scroll to settle before capturing position
			const scrollTimer = setTimeout(() => {
				const r = el.getBoundingClientRect()
				setTargetRect({ top: r.top, left: r.left, width: r.width, height: r.height, right: r.right, bottom: r.bottom })
			}, 250)

			const t1 = setTimeout(() => setIsClicking(true), CURSOR_MS + 250)
			const t2 = setTimeout(() => { setIsClicking(false); setShowTooltip(true) }, TOOLTIP_DELAY_MS + 250)
			return () => { clearTimeout(tDisplay); clearTimeout(scrollTimer); clearTimeout(t1); clearTimeout(t2) }
		}

		setTargetRect(null)
		const t1 = setTimeout(() => setIsClicking(true), CURSOR_MS)
		const t2 = setTimeout(() => { setIsClicking(false); setShowTooltip(true) }, TOOLTIP_DELAY_MS)
		return () => { clearTimeout(tDisplay); clearTimeout(t1); clearTimeout(t2) }
	}, [step])

	useEffect(() => {
		const onResize = () => {
			if (isField && current.findTarget) {
				const el = current.findTarget()
				if (el) {
					const r = el.getBoundingClientRect()
					setTargetRect({ top: r.top, left: r.left, width: r.width, height: r.height, right: r.right, bottom: r.bottom })
				}
			}
		}
		window.addEventListener('resize', onResize)
		return () => window.removeEventListener('resize', onResize)
	}, [step])

	const handleNext = useCallback(() => {
		if (waiting) return

		if (current.action) {
			setWaiting(true)
			Promise.resolve(current.action()).then(() => {
				if (current.waitFor) {
					const pollId = setInterval(() => {
						if (current.waitFor()) {
							clearInterval(pollId)
							pollRef.current = null
							setWaiting(false)
							const next = resolveStep(step)
							next !== null ? setStep(next) : onClose()
						}
					}, 500)
					pollRef.current = pollId
					setTimeout(() => {
						if (pollRef.current === pollId) {
							clearInterval(pollId)
							pollRef.current = null
							setWaiting(false)
							const next = resolveStep(step)
							next !== null ? setStep(next) : onClose()
						}
					}, 30000)
				} else {
					setWaiting(false)
					const next = resolveStep(step)
					next !== null ? setStep(next) : onClose()
				}
			})
			return
		}

		const next = resolveStep(step)
		next !== null ? setStep(next) : onClose()
	}, [step, totalSteps, onClose, current, waiting, resolveStep])

	const handleBack = useCallback(() => {
		if (waiting) return
		const prev = resolveStep(step, -1)
		if (prev !== null) {
			for (let i = step; i > prev; i--) {
				tourSteps[i]?.reverseAction?.()
			}
			const targetRange = modalRanges.find(r => prev >= r.from && prev <= r.to)
			const currentRange = modalRanges.find(r => step >= r.from && step <= r.to)
			if (targetRange && targetRange !== currentRange) {
				if (targetRange.isOpen()) {
					setStep(prev)
				} else {
					setWaiting(true)
					Promise.resolve(targetRange.open()).then(() => {
						const pollId = setInterval(() => {
							if (targetRange.isOpen()) {
								clearInterval(pollId)
								setWaiting(false)
								setStep(prev)
							}
						}, 300)
						setTimeout(() => { clearInterval(pollId); setWaiting(false); setStep(prev) }, 8000)
					})
				}
			} else if (!targetRange && currentRange && currentRange.isOpen()) {
				currentRange.close()
				setStep(prev)
			} else {
				setStep(prev)
			}
		}
	}, [step, waiting, resolveStep, tourSteps, modalRanges])

	const handleDotClick = useCallback((i) => {
		if (waiting || i === step) return
		if (i < step) {
			for (let j = step; j > i; j--) {
				tourSteps[j]?.reverseAction?.()
			}
		}
		const targetRange = modalRanges.find(r => i >= r.from && i <= r.to)
		const currentRange = modalRanges.find(r => step >= r.from && step <= r.to)
		if (targetRange && targetRange !== currentRange) {
			if (targetRange.isOpen()) {
				setStep(i)
			} else {
				setWaiting(true)
				Promise.resolve(targetRange.open()).then(() => {
					const pollId = setInterval(() => {
						if (targetRange.isOpen()) {
							clearInterval(pollId)
							setWaiting(false)
							setStep(i)
						}
					}, 300)
					setTimeout(() => { clearInterval(pollId); setWaiting(false); setStep(i) }, 8000)
				})
			}
		} else if (!targetRange && currentRange && currentRange.isOpen()) {
			currentRange.close()
			setStep(i)
		} else {
			setStep(i)
		}
	}, [step, waiting, tourSteps, modalRanges])

	useEffect(() => {
		const handleKeyDown = (e) => {
			if (waiting) return

			switch (e.key) {
				case 'ArrowRight':
				case 'Enter':
					e.preventDefault()
					handleNext()
					break
				case 'ArrowLeft':
					e.preventDefault()
					handleBack()
					break
				case 'Escape':
					e.preventDefault()
					onClose()
					break
				default:
					break
			}
		}

		document.addEventListener('keydown', handleKeyDown)
		return () => document.removeEventListener('keydown', handleKeyDown)
	}, [waiting, handleNext, handleBack, onClose])

	const rect = targetRect
	const cursorX = rect ? rect.left + rect.width * 0.45 : -50
	const cursorY = rect ? rect.top + rect.height * 0.55 : -50
	const tooltipPos = getTooltipPosition(rect)
	const isCentered = !isDisplayedField || !rect

	return <>
		{/* Click blocker */}
		<Box sx={{ position: 'fixed', inset: 0, zIndex: 9997 }} />

		{/* Spotlight or backdrop */}
		{isDisplayedField && rect
			? <Box sx={{
				position: 'fixed',
				top: rect.top - 10,
				left: rect.left - 10,
				width: rect.width + 20,
				height: rect.height + 20,
				borderRadius: '12px',
				boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.58)',
				outline: '3px solid rgba(51, 136, 181, 0.45)',
				outlineOffset: '3px',
				zIndex: 9998,
				pointerEvents: 'none',
				transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
				willChange: 'top, left, width, height',
			}} />
			: <Box sx={{
				position: 'fixed', inset: 0,
				backgroundColor: 'rgba(0, 0, 0, 0.62)',
				zIndex: 9998,
				transition: 'opacity 0.4s',
			}} />
		}

		{/* Animated cursor */}
		{isDisplayedField && rect && <Box sx={{
			position: 'fixed',
			left: cursorX,
			top: cursorY,
			zIndex: 10002,
			pointerEvents: 'none',
			transition: `left ${CURSOR_MS}ms cubic-bezier(0.4, 0, 0.2, 1), top ${CURSOR_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
			transform: isClicking ? 'scale(0.78) rotate(-8deg)' : 'scale(1) rotate(0deg)',
			transformOrigin: '2px 2px',
			willChange: 'left, top, transform',
		}}>
			<CursorSVG />
		</Box>}

		{/* Tooltip card */}
		<Fade in={showTooltip} timeout={200}>
			<Paper elevation={12} sx={{
				position: 'fixed',
				...(isCentered
					? { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }
					: tooltipPos
				),
				width: isCentered ? 460 : 370,
				zIndex: 10001,
				borderRadius: '16px',
				overflow: 'hidden',
				pointerEvents: 'all',
			}}>
				{/* Accent bar + close button */}
				<Box sx={{ position: 'relative' }}>
					<Box sx={{ height: 4, background: 'linear-gradient(90deg, #3388b5, #a4cde3)' }} />
					<Button
						size="small"
						onClick={onClose}
						sx={{
							position: 'absolute', top: 8, right: 8,
							minWidth: 28, width: 28, height: 28, p: 0,
							borderRadius: '50%',
							color: 'text.secondary',
							backgroundColor: 'rgba(0,0,0,0.06)',
							'&:hover': { backgroundColor: 'rgba(0,0,0,0.12)' },
						}}
					>
						<IconSeeker icon="xmark" sx={{ fontSize: 14 }} />
					</Button>
				</Box>

				<Box sx={{ p: 3, pt: 2 }}>
					{/* Step counter */}
					<Typography variant="caption" sx={{
						color: 'primary.main', fontWeight: 700, letterSpacing: 1.2, fontSize: '0.68rem',
					}}>
						STEP {displayedStep + 1} / {totalSteps}
					</Typography>

					{/* Icon + Title */}
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5, mb: 1.5 }}>
						<Box sx={{
							width: isCentered ? 52 : 40,
							height: isCentered ? 52 : 40,
							borderRadius: '50%',
							backgroundColor: 'primary.lighter',
							display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
						}}>
							<IconSeeker icon={displayedCurrent.icon} sx={{ fontSize: isCentered ? 22 : 17, color: 'primary.dark' }} />
						</Box>
						<Typography variant={isCentered ? 'h5' : 'h6'} sx={{ fontWeight: 700, color: 'secondary.dark', lineHeight: 1.25 }}>
							{displayedCurrent.title}
						</Typography>
					</Box>

					{/* Description */}
					<Typography variant="body2" sx={{
						color: 'secondary.light', lineHeight: 1.75, mb: displayedCurrent.example ? 2 : 0,
						fontSize: isCentered ? '0.9rem' : '0.84rem',
					}}>
						{displayedCurrent.description}
					</Typography>

					{/* Example chip */}
					{displayedCurrent.example && <Box sx={{
						backgroundColor: 'primary.lighter',
						borderRadius: '8px',
						px: 2, py: 1,
						border: '1px dashed',
						borderColor: 'primary.light',
					}}>
						<Typography variant="caption" sx={{ color: 'primary.darker', fontWeight: 600 }}>
							{displayedCurrent.example}
						</Typography>
					</Box>}

					{/* Navigation */}
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 3 }}>
						<Button
							size="small"
							disabled={step === 0 || waiting}
							onClick={handleBack}
							sx={{ textTransform: 'none', color: 'text.secondary', minWidth: 60, backgroundColor: 'rgba(0,0,0,0.06)', borderRadius: '8px', '&:hover': { backgroundColor: 'rgba(0,0,0,0.12)' } }}
						>
							Back
						</Button>

						{/* Dots */}
						<Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center', maxWidth: 160 }}>
							{tourSteps.map((_, i) => (
								<Box key={i} onClick={() => handleDotClick(i)} sx={{
									width: i === step ? 18 : 6,
									height: 6,
									borderRadius: '3px',
									backgroundColor: i === step ? 'primary.main' : i < step ? 'primary.light' : 'secondary.lighter',
									transition: 'all 0.3s ease',
									cursor: i !== step ? 'pointer' : 'default',
									'&:hover': i !== step ? { opacity: 0.7 } : {},
								}} />
							))}
						</Box>

						<Button
							variant="contained"
							size="small"
							color="primary"
							onClick={handleNext}
							disabled={waiting}
							disableElevation
							sx={{ textTransform: 'none', px: 1.5, borderRadius: '8px', minWidth: 72 }}
						>
							{waiting ? 'Loading...' : step === totalSteps - 1 ? 'Finish' : 'Next'}
						</Button>
					</Box>
				</Box>
			</Paper>
		</Fade>

		{/* Skip button */}
		<Fade in timeout={600}>
			<Box sx={{ position: 'fixed', top: 16, right: 16, zIndex: 10003 }}>
				<Button
					size="small"
					onClick={onClose}
					sx={{
						textTransform: 'none',
						color: 'rgba(255,255,255,0.85)',
						backgroundColor: 'rgba(255,255,255,0.12)',
						backdropFilter: 'blur(4px)',
						'&:hover': { backgroundColor: 'rgba(255,255,255,0.22)' },
						borderRadius: '8px', px: 2, py: 0.5,
						fontSize: '0.8rem',
					}}
				>
					Skip Tour
				</Button>
			</Box>
		</Fade>
	</>
}


// Pure presentational button — no hooks (avoids React queue bug in actions slot)
export default function VirtualTourButton({ onClick }) {
	return <Box>
		<Button
			variant="outlined"
			color="primary"
			size="small"
			startIcon={<IconSeeker icon="circle-info" />}
			onClick={onClick}
			sx={{ textTransform: 'none', borderRadius: 2, px: 2 }}
		>
			Virtual Tour
		</Button>
	</Box>
}
